import type { CommerceCustomerSession } from "@teqtank/commerce-contracts";
import { MockCommerceTransport, CommerceTransportError } from "@teqtank/commerce-transport";
import { createCommerceCustomerOperation } from "../operations/create-commerce-customer-operation.js";
import { resolveCommerceCustomerIdentityOperation } from "../operations/resolve-commerce-customer-identity-operation.js";
import { loginCommerceCustomerOperation } from "../operations/login-commerce-customer-operation.js";
import { logoutCommerceCustomerOperation } from "../operations/logout-commerce-customer-operation.js";
import { requestCommerceCustomerVerificationCodeOperation } from "../operations/request-commerce-customer-verification-code-operation.js";
import { verifyCommerceCustomerCodeOperation } from "../operations/verify-commerce-customer-code-operation.js";
import { refreshCommerceCustomerSessionOperation } from "../operations/refresh-commerce-customer-session-operation.js";
import { getCommerceCustomerRegistrationOperation } from "../operations/get-commerce-customer-registration-operation.js";
import { searchCommerceCustomerSponsorsOperation } from "../operations/search-commerce-customer-sponsors-operation.js";
import { getCommerceCustomerActionsOperation } from "../operations/get-commerce-customer-actions-operation.js";
import { launchCommerceCustomerActionOperation } from "../operations/launch-commerce-customer-action-operation.js";

export function registerDefaultCustomerOperations(transport: MockCommerceTransport): void {
    const sessions = new Map<string, CommerceCustomerSession>();
    transport.register(getCommerceCustomerActionsOperation, () => ({
        actions: Object.freeze([
            Object.freeze({
                id: "my-account",
                label: "My Account",
                description: "View and update your account.",
            }),
            Object.freeze({
                id: "view-orders",
                label: "View Orders",
                description: "Review your order history.",
            }),
            Object.freeze({
                id: "back-office",
                label: "Back Office",
                description: "Open your Back Office home.",
            }),
        ]),
    }));
    transport.register(launchCommerceCustomerActionOperation, ({ actionId }) => ({
        url: `https://backoffice.teqtank.com/Account/Login?sso=playground&ReturnUrl=${encodeURIComponent(actionId === "back-office" ? "/" : actionId === "view-orders" ? "/Orders/History" : "/User/Profile")}`,
    }));
    transport.register(getCommerceCustomerRegistrationOperation, () => ({
        registration: {
            passwordMode: "optional" as const,
            identityFields: ["email", "phone"] as const,
            sponsor: { mode: "optional" as const, allowNoSponsor: true, attributionApplied: false },
            verification: { provider: "none", siteKey: null },
            consents: Object.freeze([]),
        },
    }));
    transport.register(searchCommerceCustomerSponsorsOperation, ({ search }) => ({
        sponsors:
            search.trim().length < 2
                ? Object.freeze([])
                : Object.freeze([
                      Object.freeze({ id: "1001", webAlias: "demo", displayName: "Demo Sponsor" }),
                  ]),
    }));
    transport.register(resolveCommerceCustomerIdentityOperation, ({ email }) => ({
        status: email.endsWith("@example.test")
            ? ("authentication-required" as const)
            : ("new-customer" as const),
    }));
    transport.register(createCommerceCustomerOperation, (request) => {
        const now = new Date();
        const id = `customer:${request.email.toLowerCase()}`;
        const session: CommerceCustomerSession = Object.freeze({
            status: "authenticated",
            customer: Object.freeze({
                id,
                email: request.email,
                firstName: request.firstName,
                lastName: request.lastName,
                displayName: `${request.firstName} ${request.lastName}`.trim(),
                initials:
                    `${request.firstName[0] ?? "C"}${request.lastName[0] ?? ""}`.toUpperCase(),
                phone: request.phone,
                addresses: Object.freeze([]),
                savedPaymentMethods: Object.freeze([]),
            }),
            authenticatedAt: now,
            expiresAt: new Date(now.getTime() + 86400000),
            rememberMe: false,
            backOfficeUrl: null,
        });
        sessions.set(id, session);
        return { session };
    });
    transport.register(loginCommerceCustomerOperation, (request) => {
        if (request.password === "invalid")
            throw new CommerceTransportError(
                "request-failed",
                "The email or password was not recognised.",
                { operationName: loginCommerceCustomerOperation.name },
            );
        const email = request.identifier.includes("@")
            ? request.identifier
            : `${request.identifier}@example.test`;
        const name = deriveName(email);
        const id = `customer:${email.toLowerCase()}`;
        const now = new Date();
        const session: CommerceCustomerSession = Object.freeze({
            status: "authenticated",
            customer: Object.freeze({
                id,
                email,
                firstName: name.firstName,
                lastName: name.lastName,
                displayName: `${name.firstName} ${name.lastName}`.trim(),
                initials: `${name.firstName[0] ?? "C"}${name.lastName[0] ?? ""}`.toUpperCase(),
                addresses: Object.freeze([]),
                savedPaymentMethods: createSavedPaymentMethods(id, name.firstName, name.lastName),
            }),
            authenticatedAt: now,
            expiresAt: new Date(
                now.getTime() + (request.rememberMe ? 30 : 1) * 24 * 60 * 60 * 1000,
            ),
            rememberMe: request.rememberMe ?? false,
            backOfficeUrl: "https://backoffice.teqtank.com",
        });
        sessions.set(id, session);
        return { session };
    });
    const challenges = new Map<string, string>();

    transport.register(requestCommerceCustomerVerificationCodeOperation, ({ email }) => {
        const challengeId = `verify:${email.toLowerCase()}`;
        challenges.set(challengeId, email);
        return {
            challengeId,
            maskedDestination: email,
            expiresAt: new Date(Date.now() + 10 * 60 * 1000),
        };
    });

    transport.register(verifyCommerceCustomerCodeOperation, ({ challengeId, code, rememberMe }) => {
        const email = challenges.get(challengeId);
        if (!email || code !== "123456") {
            throw new CommerceTransportError(
                "request-failed",
                "The verification code was not recognised.",
                { operationName: verifyCommerceCustomerCodeOperation.name },
            );
        }

        const name = deriveName(email);
        const id = `customer:${email.toLowerCase()}`;
        const now = new Date();
        const session: CommerceCustomerSession = Object.freeze({
            status: "authenticated",
            customer: Object.freeze({
                id,
                email,
                firstName: name.firstName,
                lastName: name.lastName,
                displayName: `${name.firstName} ${name.lastName}`.trim(),
                initials: `${name.firstName[0] ?? "C"}${name.lastName[0] ?? ""}`.toUpperCase(),
                addresses: Object.freeze([]),
                savedPaymentMethods: createSavedPaymentMethods(id, name.firstName, name.lastName),
            }),
            authenticatedAt: now,
            expiresAt: new Date(now.getTime() + (rememberMe ? 30 : 1) * 86400000),
            rememberMe: rememberMe ?? false,
            backOfficeUrl: null,
        });

        challenges.delete(challengeId);
        sessions.set(id, session);
        return { session };
    });
    transport.register(refreshCommerceCustomerSessionOperation, ({ customerId }) => ({
        session: sessions.get(customerId ?? "") ?? expiredSession(),
    }));
    transport.register(logoutCommerceCustomerOperation, ({ customerId }) => {
        if (customerId) sessions.delete(customerId);
        return { session: anonymousSession() };
    });
}
function createSavedPaymentMethods(customerId: string, firstName: string, lastName: string) {
    return Object.freeze([
        Object.freeze({
            id: `payment:${customerId}:visa`,
            token: `tok_mock_${customerId.replace(/[^a-z0-9]/gi, "_")}`,
            type: "card",
            displayName: "Visa •••• 4242",
            cardholderName: `${firstName} ${lastName}`.trim(),
            brand: "Visa",
            lastFour: "4242",
            expiryMonth: 8,
            expiryYear: 2029,
            isDefault: true,
        }),
    ]);
}

function deriveName(email: string): { firstName: string; lastName: string } {
    const source = email.split("@")[0] ?? "customer";
    const parts = source.split(/[._-]+/).filter(Boolean);
    return {
        firstName: title(parts[0] ?? "Customer"),
        lastName: title(parts[1] ?? ""),
    };
}
function title(value: string): string {
    return value ? value[0]!.toUpperCase() + value.slice(1).toLowerCase() : "";
}
function anonymousSession(): CommerceCustomerSession {
    return Object.freeze({
        status: "anonymous",
        customer: null,
        authenticatedAt: null,
        expiresAt: null,
        rememberMe: false,
        backOfficeUrl: null,
    });
}
function expiredSession(): CommerceCustomerSession {
    return Object.freeze({ ...anonymousSession(), status: "expired" });
}
